import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Crown, 
  Gem, 
  Shield, 
  Zap, 
  Gift, 
  Star, 
  Rocket,
  Lock,
  Key,
  Award,
  TrendingUp,
  Users,
  Calendar,
  Download
} from 'lucide-react';

interface NFTReward {
  id: string;
  name: string;
  description: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  image: string;
  requirement: string;
  claimed: boolean;
  unlocked: boolean;
}

interface ExclusiveFeature {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  tier: 'bronze' | 'silver' | 'gold' | 'diamond';
  requirement: string;
  unlocked: boolean;
}

interface VaultAlliance {
  partnerName: string;
  description: string;
  benefits: string[];
  icon: React.ReactNode;
}

export function NDHExclusiveHub() {
  const [ndhBalance] = useState(2500); // User's NDH balance
  const [holderTier] = useState('gold'); // bronze, silver, gold, diamond
  const [stakingRewards] = useState(125.50);

  const exclusiveFeatures: ExclusiveFeature[] = [
    {
      id: '1',
      title: 'Priority Trading',
      description: 'Get front-of-the-line access to new token launches and exclusive pre-sales',
      icon: <Zap className="h-6 w-6" />,
      tier: 'bronze',
      requirement: '1,000+ NDH',
      unlocked: true
    },
    {
      id: '2',
      title: 'Advanced Analytics',
      description: 'Access to institutional-grade market analysis and AI trading signals',
      icon: <TrendingUp className="h-6 w-6" />,
      tier: 'silver',
      requirement: '2,500+ NDH',
      unlocked: true
    },
    {
      id: '3',
      title: 'VIP Support',
      description: '24/7 dedicated support with direct access to development team',
      icon: <Crown className="h-6 w-6" />,
      tier: 'gold',
      requirement: '5,000+ NDH',
      unlocked: false
    },
    {
      id: '4',
      title: 'Genesis Vault Access',
      description: 'Exclusive access to INFRA VAULT CORE partnership benefits',
      icon: <Shield className="h-6 w-6" />,
      tier: 'diamond',
      requirement: '10,000+ NDH',
      unlocked: false
    }
  ];

  const nftRewards: NFTReward[] = [
    {
      id: '1',
      name: 'Diamond Hands',
      description: 'Awarded for holding NDH for 365 days',
      rarity: 'legendary',
      image: '💎',
      requirement: 'Hold 1000+ NDH for 1 year',
      claimed: false,
      unlocked: false
    },
    {
      id: '2',
      name: 'Trading Master',
      description: 'Complete 1000 successful trades',
      rarity: 'epic',
      image: '🏆',
      requirement: '1000 profitable trades',
      claimed: false,
      unlocked: true
    },
    {
      id: '3',
      name: 'Early Adopter',
      description: 'One of the first 1000 NDH holders',
      rarity: 'rare',
      image: '🚀',
      requirement: 'Top 1000 holders',
      claimed: true,
      unlocked: true
    }
  ];

  const vaultAlliance: VaultAlliance = {
    partnerName: 'INFRA VAULT CORE',
    description: 'Strategic partnership providing institutional-grade infrastructure and exclusive yield farming opportunities',
    benefits: [
      'Access to institutional liquidity pools',
      'Reduced gas fees across all networks',
      'Priority access to new vault strategies',
      'Exclusive INFRA token airdrops',
      'Advanced risk management tools',
      'Professional portfolio analytics'
    ],
    icon: <Shield className="h-8 w-8" />
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'diamond': return 'from-cyan-400 to-blue-600';
      case 'gold': return 'from-yellow-400 to-orange-500';
      case 'silver': return 'from-gray-300 to-gray-500';
      case 'bronze': return 'from-orange-400 to-red-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getRarityGradient = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'from-purple-400 via-pink-500 to-yellow-500';
      case 'epic': return 'from-purple-500 to-indigo-600';
      case 'rare': return 'from-blue-500 to-cyan-600';
      case 'common': return 'from-gray-400 to-gray-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getNextTierRequirement = () => {
    switch (holderTier) {
      case 'bronze': return { tier: 'silver', amount: 2500 };
      case 'silver': return { tier: 'gold', amount: 5000 };
      case 'gold': return { tier: 'diamond', amount: 10000 };
      default: return null;
    }
  };

  const nextTier = getNextTierRequirement();

  return (
    <div className="space-y-6">
      {/* NDH Holder Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`bg-gradient-to-r ${getTierColor(holderTier)} text-white`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Holder Tier</p>
                <p className="text-2xl font-bold capitalize">{holderTier}</p>
              </div>
              <Crown className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">NDH Balance</p>
                <p className="text-2xl font-bold">{ndhBalance.toLocaleString()}</p>
              </div>
              <Gem className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Rewards Earned</p>
                <p className="text-2xl font-bold">${stakingRewards}</p>
              </div>
              <Gift className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tier Progress */}
      {nextTier && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Progress to {nextTier.tier} Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Current: {ndhBalance.toLocaleString()} NDH</span>
                <span>Required: {nextTier.amount.toLocaleString()} NDH</span>
              </div>
              <Progress value={(ndhBalance / nextTier.amount) * 100} className="h-3" />
              <p className="text-sm text-muted-foreground">
                You need {(nextTier.amount - ndhBalance).toLocaleString()} more NDH to reach {nextTier.tier} tier
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="features" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="features">Exclusive Features</TabsTrigger>
          <TabsTrigger value="nfts">NFT Rewards</TabsTrigger>
          <TabsTrigger value="vault">Vault Alliance</TabsTrigger>
          <TabsTrigger value="benefits">Benefits</TabsTrigger>
        </TabsList>

        <TabsContent value="features" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Exclusive NDH Holder Features</CardTitle>
              <CardDescription>
                Unlock premium features based on your NDH holdings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {exclusiveFeatures.map((feature) => (
                <div key={feature.id} className={`border-2 rounded-lg p-4 ${
                  feature.unlocked ? 'border-green-500 bg-green-50 dark:bg-green-950' : 'border-gray-300 bg-gray-50 dark:bg-gray-900'
                }`}>
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg bg-gradient-to-r ${getTierColor(feature.tier)}`}>
                      {feature.icon}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{feature.title}</h4>
                        <div className="flex items-center gap-2">
                          <Badge className={`bg-gradient-to-r ${getTierColor(feature.tier)} text-white`}>
                            {feature.tier}
                          </Badge>
                          {feature.unlocked ? (
                            <Key className="h-4 w-4 text-green-500" />
                          ) : (
                            <Lock className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-2">{feature.description}</p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Requirement: {feature.requirement}</span>
                        {feature.unlocked ? (
                          <Badge variant="default" className="bg-green-500">
                            <Award className="h-3 w-3 mr-1" />
                            Unlocked
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            Locked
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="nfts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Exclusive NFT Collection</CardTitle>
              <CardDescription>
                Earn unique NFTs by achieving milestones and holding NDH
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {nftRewards.map((nft) => (
                  <div key={nft.id} className={`border-2 rounded-lg p-4 bg-gradient-to-br ${getRarityGradient(nft.rarity)}`}>
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-4 space-y-3">
                      <div className="text-center">
                        <div className="text-6xl mb-2">{nft.image}</div>
                        <h4 className="font-bold">{nft.name}</h4>
                        <Badge className={`bg-gradient-to-r ${getRarityGradient(nft.rarity)} text-white`}>
                          {nft.rarity}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-center text-muted-foreground">{nft.description}</p>
                      
                      <div className="space-y-2">
                        <p className="text-xs font-medium">Requirement:</p>
                        <p className="text-xs text-muted-foreground">{nft.requirement}</p>
                      </div>
                      
                      <div className="flex justify-center">
                        {nft.claimed ? (
                          <Badge variant="default" className="bg-green-500">
                            <Award className="h-3 w-3 mr-1" />
                            Claimed
                          </Badge>
                        ) : nft.unlocked ? (
                          <Button size="sm" className="w-full">
                            Claim NFT
                          </Button>
                        ) : (
                          <Badge variant="secondary">
                            <Lock className="h-3 w-3 mr-1" />
                            Locked
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vault" className="space-y-4">
          <Card className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                {vaultAlliance.icon}
                Strategic Alliance: {vaultAlliance.partnerName}
              </CardTitle>
              <CardDescription className="text-blue-100">
                {vaultAlliance.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {vaultAlliance.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white/10 rounded-lg">
                    <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-4 mt-6">
                <Button className="flex-1 bg-white text-blue-600 hover:bg-blue-50">
                  <Rocket className="h-4 w-4 mr-2" />
                  Access Vault Features
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  <Download className="h-4 w-4 mr-2" />
                  Download Guide
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Alliance Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">$2.4M</div>
                <p className="text-sm text-muted-foreground">Total Value Locked</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">18.5%</div>
                <p className="text-sm text-muted-foreground">Average APY</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">1,247</div>
                <p className="text-sm text-muted-foreground">Active Users</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="benefits" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-green-600">Staking Rewards</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span>Base APY</span>
                  <span className="font-bold">12.5%</span>
                </div>
                <div className="flex justify-between">
                  <span>Holder Bonus</span>
                  <span className="font-bold text-green-600">+3.2%</span>
                </div>
                <div className="flex justify-between">
                  <span>Vault Alliance Bonus</span>
                  <span className="font-bold text-blue-600">+2.8%</span>
                </div>
                <hr />
                <div className="flex justify-between text-lg">
                  <span>Total APY</span>
                  <span className="font-bold text-green-600">18.5%</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-blue-600">Trading Benefits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span>Trading Fees</span>
                  <span className="font-bold">-50%</span>
                </div>
                <div className="flex justify-between">
                  <span>Gas Fee Discount</span>
                  <span className="font-bold text-blue-600">-25%</span>
                </div>
                <div className="flex justify-between">
                  <span>Priority Execution</span>
                  <span className="font-bold text-green-600">✓</span>
                </div>
                <div className="flex justify-between">
                  <span>Advanced Analytics</span>
                  <span className="font-bold text-green-600">✓</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Upcoming Benefits</CardTitle>
              <CardDescription>
                New features coming soon for NDH holders
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { feature: 'Governance Voting Rights', eta: 'Q1 2024', status: 'development' },
                { feature: 'Cross-chain Bridge Priority', eta: 'Q2 2024', status: 'planning' },
                { feature: 'AI Trading Bot Access', eta: 'Q2 2024', status: 'beta' },
                { feature: 'Insurance Coverage', eta: 'Q3 2024', status: 'planning' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{item.feature}</p>
                    <p className="text-sm text-muted-foreground">ETA: {item.eta}</p>
                  </div>
                  <Badge variant={item.status === 'beta' ? 'default' : 'secondary'}>
                    {item.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}